import { memo, CSSProperties, useCallback } from 'react'
import { useHistory } from 'react-router-dom'

import Card from 'components/common/Card'
import MaleIcon from 'assets/icons/gender/Male'
import FemaleIcon from 'assets/icons/gender/Female'
import { useTranslation } from 'services/Translation'
import { CS, CS_m } from 'back-end-api'
import { ROUTES } from 'Constants'
import { getSummaryStatusText, getSummaryStatusColor, useStatusTranslation } from 'services/Registrations'

const texts = (getText: (key: string, category: string) => string) => (key: string) =>
  ({
    medicalQuestion: getText('Medical question', 'Sidebar_Encounter_Element'),
  }[key])

interface ElementProps {
  registration: CS_m.ModelsRegistration
  style: CSSProperties
}

const Element = ({ registration, style }: ElementProps) => {
  const history = useHistory()
  const { formatDate, t } = useTranslation(texts)
  const tStatus = useStatusTranslation()

  const goToRegistration = useCallback(() => {
    history.push(`${ROUTES.PATIENT}/${registration.id}`)
  }, [history, registration])

  const summary = CS.getSummary(registration)
  const appointment = CS.getAppointment(summary)

  return (
    <Card
      withMark
      markColor="#6C5BFF"
      className="mt-7 p-1.5 flex items-stretch hover:shadow-cxl transition"
      onClick={goToRegistration}
      style={{
        height: '4.5rem',
        ...style,
      }}
      innerRef={undefined}
      animate={undefined}
      variants={undefined}
      initial={undefined}
    >
      {/* Left: date/time column */}
      <div
        className="text-purple-200 h-full border-r pr-3 mr-3 justify-center border-grey-100 font-csc65 items-center flex flex-col flex-shrink-0"
        style={{ fontSize: '0.800rem', width: '5.5rem', textAlign: 'center' }}
      >
        <span>{formatDate(new Date(registration.created_at), 'd MMMM')}</span>
        <span>{formatDate(new Date(registration.created_at), 'h:mm a')}</span>
      </div>

      {/* Right: stacked content rows */}
      <div className="flex flex-col justify-center flex-1 min-w-0">
        {/* Row 1: name + gender (left), status (right) */}
        <div className="flex items-center justify-between w-full">
          <div className="text-blue-900 font-csc65 flex items-center min-w-0" style={{ fontSize: '0.800rem' }}>
            <span className="truncate">{`${registration.first_name || ''} ${
              registration.last_name || ''
            }`}</span>
            {registration.gender === 'male' ? (
              <MaleIcon className="ml-2.5 flex-shrink-0" />
            ) : (
              <FemaleIcon className="ml-2.5 -mt-1 flex-shrink-0" />
            )}
          </div>
          <div
            style={{ fontSize: '0.625rem', color: getSummaryStatusColor(registration), lineHeight: 1.2 }}
            className="font-csc45 flex justify-center items-center flex-shrink-0 ml-2"
          >
            {appointment && (
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4, flexShrink: 0 }}>
                <polygon points="23 7 16 12 23 17 23 7" />
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
              </svg>
            )}
            {getSummaryStatusText(registration, tStatus)}
          </div>
        </div>

        {/* Row 2: complaint (truncated, full width) */}
        <div className="text-blue-900 font-csc45 truncate w-full mt-1" style={{ fontSize: '0.6875rem' }}>
          {CS.getSummary(registration)?.complaint?.name || t('medicalQuestion')}
        </div>
      </div>
    </Card>
  )
}

Element.whyDidYouRender = true

export default memo(Element)
